import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  FileText,
  ExternalLink,
  Eye,
  AlertTriangle,
  ShieldAlert,
  Layers,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import FilingViewer from '@/components/FilingViewer';
import StarFilingButton from '@/components/StarFilingButton';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round 92o++++++++N++++++++ — Module-level cache so that re-opening
// the Stock Modal for a ticker we've already loaded once renders
// instantly with no "Loading…" flicker. The backend Mongo cache is
// already fast (~150ms on hit), but the user expectation after one
// successful load is "this should be instant the next time I click
// the bell". Two maps so 10-K/8-K and risk-factors evict independently.
//
// Lives outside the component so it survives modal unmount/remount.
// Cleared with `clearTenK8KCache(ticker)` (exported) when we want to
// force a refresh — currently nothing calls it (the server-side
// 30-day cache is the right freshness boundary).
const TENK_8K_CACHE = new Map();
const RISK_FACTORS_CACHE = new Map();

// Round 98q++ (Jul 2026) — 8-K Item-code → 5-category map. Mirror of
// backend `ITEM_TO_CATEGORY` in `server.py:25499`. Any unmapped Item
// code collapses into "other" via the fallback in `categoryFor8K()`
// below. Kept inline (not imported) so this tab has zero cross-cutting
// dependencies on backend constants.
const EIGHTK_ITEM_TO_CATEGORY = {
  'Item 1.01': 'ma',          // Material Contracts / M&A
  'Item 2.01': 'ma',          // Acquisitions / Dispositions
  'Item 2.02': 'earnings',    // Results of Operations
  'Item 5.02': 'leadership',  // Departure of Officers / Directors
  'Item 8.01': 'material',    // Other Material Events (often dividend declarations)
};
const EIGHTK_CATEGORY_LABEL = {
  earnings: 'Earnings',
  leadership: 'Leadership',
  ma: 'M&A',
  material: 'Material',
  other: 'Other',
};
// A filing carries multiple Item codes; return the set of categories
// it belongs to. Filings with no item codes fall into "other".
// Round 98q++ hotfix — Massive sometimes returns item codes with a
// thin-space (U+2009) or nbsp between "Item" and the number instead
// of a regular ASCII space. Normalize any Unicode whitespace run to
// a single ASCII space before the map lookup.
function categoriesFor8K(itemCodes) {
  if (!Array.isArray(itemCodes) || itemCodes.length === 0) return new Set(['other']);
  const out = new Set();
  for (const raw of itemCodes) {
    const c = typeof raw === 'string' ? raw.replace(/\s+/g, ' ').trim() : '';
    out.add(EIGHTK_ITEM_TO_CATEGORY[c] || 'other');
  }
  return out;
}

export function clearTenK8KCache(ticker) {
  if (ticker) {
    TENK_8K_CACHE.delete(ticker.toUpperCase());
    RISK_FACTORS_CACHE.delete(ticker.toUpperCase());
  } else {
    TENK_8K_CACHE.clear();
    RISK_FACTORS_CACHE.clear();
  }
}

/**
 * TenK8KRisksTab — Round 92o++++++++N++++++++ (May 2026).
 *
 * Premium tab that consolidates SEC filing reading material for a
 * stock into one place:
 *   • Most recent 10-K narrative (Business + Risk Factors)
 *   • Latest 8-K material events (with category badges)
 *   • Standardised Risk Factors with Massive's 3-level taxonomy
 *
 * This tab supersedes the "Recent SEC Filings" panel that used to
 * live inside MyPositionTab — extracted so non-owners and watchers
 * can also use it once the paywall ships. For now the tab itself is
 * admin-gated upstream in StockModal as a paywall proxy.
 *
 * Deep-link support: the parent passes `highlightAccession` to
 * scroll/pulse a specific 8-K when navigated to from the
 * FilingAlertsBell.
 */
function humaniseSlug(s) {
  if (!s) return '';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function RiskCategoryBadge({ primary, secondary, tertiary, taxonomyIndex }) {
  const key = `${primary || ''}|${secondary || ''}|${tertiary || ''}`;
  const desc = taxonomyIndex?.[key] || '';
  return (
    <div className="flex flex-wrap items-center gap-1 text-[10px]" title={desc}>
      {primary && (
        <span className="px-1.5 py-0.5 rounded-full border border-violet-300/60 dark:border-violet-800/60 bg-violet-50/60 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 font-bold">
          {humaniseSlug(primary)}
        </span>
      )}
      {secondary && (
        <span className="px-1.5 py-0.5 rounded-full border border-sky-300/60 dark:border-sky-800/60 bg-sky-50/60 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300 font-medium">
          {humaniseSlug(secondary)}
        </span>
      )}
      {tertiary && (
        <span className="px-1.5 py-0.5 rounded-full border border-border bg-muted/40 text-muted-foreground">
          {humaniseSlug(tertiary)}
        </span>
      )}
    </div>
  );
}

/**
 * RiskTrendBars — YoY mini-chart at the top of the Risk Factors panel.
 *
 * Round 92o++++++++N++++++++ (May 2026 — bucketing-tuning round). For
 * tickers where Massive returns rows from 2+ distinct 10-K filings,
 * we surface a horizontal-bar visualization of how each primary
 * category's row count moved year-over-year. Rising counts (rose)
 * imply the company is bracing investors for more risk in that
 * bucket; falling counts (emerald) signal an issue was resolved
 * or downgraded.
 *
 * Capped to top 6 movers by default with a "Show all" toggle —
 * keeps the panel tight when categories barely changed but lets
 * power users see the full diff when something interesting is
 * happening below the fold.
 */
function RiskTrendBars({ trend, onCategoryClick, selectedCategory }) {
  const [showAll, setShowAll] = useState(false);
  if (!trend || !Array.isArray(trend.categories) || trend.categories.length === 0) {
    return null;
  }
  const all = trend.categories;
  const visible = showAll ? all : all.slice(0, 6);
  const maxCount = Math.max(
    1,
    ...all.map((c) => Math.max(c.curr_count || 0, c.prev_count || 0)),
  );

  return (
    <div
      className="px-3 py-2.5 border-b bg-gradient-to-br from-fuchsia-50/30 to-transparent dark:from-fuchsia-950/10"
      data-testid="risk-trend-bars"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <TrendingUp className="w-3.5 h-3.5 text-fuchsia-600 dark:text-fuchsia-400" />
        <span className="text-[11px] font-bold uppercase tracking-wide">YoY Disclosure Trend</span>
        <span className="text-[10px] text-muted-foreground">
          · {trend.current_filing_date} vs {trend.previous_filing_date}
        </span>
      </div>
      <div className="space-y-1">
        {visible.map((c) => {
          const isUp = c.delta > 0;
          const isDown = c.delta < 0;
          const isSelected = selectedCategory === c.name;
          const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
          const accent = isUp
            ? 'text-rose-600 dark:text-rose-400'
            : isDown
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-zinc-500 dark:text-zinc-400';
          const barBg = isUp
            ? 'bg-rose-500/70 dark:bg-rose-400/60'
            : isDown
            ? 'bg-emerald-500/70 dark:bg-emerald-400/60'
            : 'bg-zinc-400/60 dark:bg-zinc-500/50';
          const prevPct = (c.prev_count / maxCount) * 100;
          const currPct = (c.curr_count / maxCount) * 100;
          const deltaLabel = c.delta === 0
            ? '0'
            : c.delta_pct === null
            ? `+${c.delta} (NEW)`
            : `${c.delta > 0 ? '+' : ''}${c.delta} (${c.delta_pct > 0 ? '+' : ''}${c.delta_pct}%)`;
          return (
            <button
              type="button"
              key={c.name}
              onClick={() => onCategoryClick?.(c.name)}
              className={`w-full text-left rounded px-1.5 py-1 transition-colors ${
                isSelected
                  ? 'bg-fuchsia-100/70 dark:bg-fuchsia-950/30 ring-1 ring-fuchsia-300/60 dark:ring-fuchsia-700/60'
                  : 'hover:bg-muted/40'
              }`}
              data-testid={`risk-trend-row-${c.name}`}
              title="Click to filter the risk-factor list to this category"
            >
              <div className="flex items-center gap-2">
                <Icon className={`w-3 h-3 flex-shrink-0 ${accent}`} />
                <span className="text-[11px] font-semibold flex-1 truncate">
                  {humaniseSlug(c.name)}
                </span>
                <span className="text-[10px] tabular-nums text-muted-foreground whitespace-nowrap">
                  {c.prev_count} → {c.curr_count}
                </span>
                <span className={`text-[10px] font-bold tabular-nums whitespace-nowrap ${accent}`}>
                  {deltaLabel}
                </span>
              </div>
              {/* Twin bars: prev (faded) above curr (saturated) */}
              <div className="mt-1 ml-5 flex flex-col gap-[2px]">
                <div className="h-[3px] rounded-full bg-muted/40 overflow-hidden">
                  <div
                    className="h-full bg-zinc-400/50 dark:bg-zinc-500/40 rounded-full transition-all"
                    style={{ width: `${prevPct}%` }}
                  />
                </div>
                <div className="h-[3px] rounded-full bg-muted/40 overflow-hidden">
                  <div
                    className={`h-full ${barBg} rounded-full transition-all`}
                    style={{ width: `${currPct}%` }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {all.length > 6 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-2 text-[10px] font-semibold text-fuchsia-700 dark:text-fuchsia-400 hover:underline"
          data-testid="risk-trend-toggle"
        >
          {showAll ? '← Show top 6' : `Show all ${all.length} categories →`}
        </button>
      )}
    </div>
  );
}

export default function TenK8KRisksTab({ ticker, highlightAccession }) {
  const { user } = useAuth();
  const [filings, setFilings] = useState({ tenK: null, eightK: null, loading: true, loadingTenK: true, loadingEightK: true });
  const [riskData, setRiskData] = useState({ rows: null, taxonomyIndex: {}, trend: null, loading: true, error: null });
  const [expandedSections, setExpandedSections] = useState({});
  const [riskFilter, setRiskFilter] = useState(null); // selected primary_category
  const [eightKFilter, setEightKFilter] = useState(null); // selected 8-K category (earnings/leadership/ma/material/other)
  const [riskExpanded, setRiskExpanded] = useState({});
  const [viewerFiling, setViewerFiling] = useState(null);
  const containerRef = useRef(null);
  const eightKRefs = useRef({});

  // Parallel fetch on mount, with module-level cache so reopening
  // the modal for a ticker we've already seen renders instantly.
  useEffect(() => {
    if (!ticker) return;
    const upper = ticker.toUpperCase();
    let cancelled = false;

    // Hydrate from cache first — instant render on repeat opens.
    const cachedFilings = TENK_8K_CACHE.get(upper);
    const cachedRisk = RISK_FACTORS_CACHE.get(upper);
    if (cachedFilings) {
      setFilings({ ...cachedFilings, loading: false, loadingTenK: false, loadingEightK: false });
    } else {
      setFilings({ tenK: null, eightK: null, loading: true, loadingTenK: true, loadingEightK: true });
    }
    if (cachedRisk) {
      setRiskData({ ...cachedRisk, loading: false, error: null });
    } else {
      setRiskData({ rows: null, taxonomyIndex: {}, trend: null, loading: true, error: null });
    }

    // Round 92o++++++++N++++++++++ (May 2026 perf fix) — split the
    // 10-K and 8-K fetches so the 8-K (cheap, fast) renders as soon
    // as it lands instead of waiting for the 10-K. Previously these
    // two were joined in a Promise.all and the user had to stare at
    // the empty 8-K panel while the slower 10-K resolved. Risk
    // Factors continues to fire in parallel since it has its own
    // panel and shouldn't block either of the upper two.
    if (!cachedFilings) {
      // 8-K FIRST — fast, gives the user something useful immediately.
      axios.get(`${API}/stocks/${ticker}/filings/8-k-events?limit=10`, { withCredentials: true })
        .then((r) => r.data).catch(() => ({ events: [] }))
        .then((eightK) => {
          if (cancelled) return;
          setFilings((prev) => {
            const next = { ...prev, eightK, loadingEightK: false };
            // Loading is "done" only when both are in. Cache too.
            next.loading = next.loadingTenK;
            if (!next.loadingTenK) {
              TENK_8K_CACHE.set(upper, { tenK: next.tenK, eightK });
            }
            return next;
          });
        });
      // 10-K SECOND — slower (SEC + Massive composite). Renders below
      // the 8-K, so a small lag here doesn't block the user reading
      // the latest material events.
      axios.get(`${API}/stocks/${ticker}/filings/10-k-sections?sections=business,risk_factors&limit=1`, { withCredentials: true })
        .then((r) => r.data).catch(() => ({ sections: [] }))
        .then((tenK) => {
          if (cancelled) return;
          setFilings((prev) => {
            const next = { ...prev, tenK, loadingTenK: false };
            next.loading = next.loadingEightK;
            if (!next.loadingEightK) {
              TENK_8K_CACHE.set(upper, { tenK, eightK: next.eightK });
            }
            return next;
          });
        });
    }

    // Risk Factors LAST priority — fired in parallel with the above
    // (HTTP/2 multiplexes, browser concurrency is plenty), but its
    // own state slice means it never blocks the 8-K / 10-K render.
    if (!cachedRisk) {
      axios.get(`${API}/stocks/${ticker}/risk-factors?limit=100`, { withCredentials: true })
        .then((r) => {
          if (cancelled) return;
          const next = {
            rows: Array.isArray(r.data?.rows) ? r.data.rows : [],
            taxonomyIndex: r.data?.taxonomy_index || {},
            trend: r.data?.trend || null,
          };
          RISK_FACTORS_CACHE.set(upper, next);
          setRiskData({ ...next, loading: false, error: null });
        })
        .catch((err) => {
          if (cancelled) return;
          setRiskData({
            rows: [],
            taxonomyIndex: {},
            trend: null,
            loading: false,
            error: err?.response?.data?.detail || 'Failed to load risk factors.',
          });
        });
    }

    return () => { cancelled = true; };
  }, [ticker]);

  // Group risk factors by primary_category for the filter chips +
  // grouped rendering. Memoised so toggling the filter doesn't
  // re-walk the dataset on every render.
  const { riskGroups, primaryCategories, totalRiskCount, visibleRows } = useMemo(() => {
    const rows = riskData.rows || [];
    const groups = {};
    for (const r of rows) {
      const cat = r.primary_category || 'uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(r);
    }
    const cats = Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length);
    const visible = riskFilter ? (groups[riskFilter] || []) : rows;
    return {
      riskGroups: groups,
      primaryCategories: cats,
      totalRiskCount: rows.length,
      visibleRows: visible,
    };
  }, [riskData.rows, riskFilter]);

  // Group 8-K events by category so the filter chip bar can show
  // counts per bucket. A single filing can belong to multiple
  // categories (e.g. a 2.02 earnings release that also carries an
  // 8.01 dividend declaration), so it appears in each of its
  // category buckets — but the visible list dedups by row.
  const { eightKCategoryCounts, eightKCategoriesPresent, eightKTotalCount, visibleEightK } = useMemo(() => {
    const events = (filings.eightK && Array.isArray(filings.eightK.events)) ? filings.eightK.events : [];
    const counts = { earnings: 0, leadership: 0, ma: 0, material: 0, other: 0 };
    for (const e of events) {
      const cats = categoriesFor8K(e.item_codes);
      for (const c of cats) counts[c] = (counts[c] || 0) + 1;
    }
    const present = Object.keys(counts).filter((c) => counts[c] > 0);
    // Sort by count descending, then by fixed label order for stability.
    const orderIndex = { earnings: 0, ma: 1, leadership: 2, material: 3, other: 4 };
    present.sort((a, b) => (counts[b] - counts[a]) || (orderIndex[a] - orderIndex[b]));
    const visible = eightKFilter
      ? events.filter((e) => categoriesFor8K(e.item_codes).has(eightKFilter))
      : events;
    return {
      eightKCategoryCounts: counts,
      eightKCategoriesPresent: present,
      eightKTotalCount: events.length,
      visibleEightK: visible,
    };
  }, [filings.eightK, eightKFilter]);

  // Deep-link scroll + highlight effect. When the parent passes a
  // highlightAccession that matches a loaded 8-K row, scroll the row
  // into view and apply the `filing-highlight-pulse` class for ~3s.
  // The accession formatting varies between the bell's notification
  // store and Massive's 8-K endpoint (with/without dashes), so we
  // also try the dash-stripped variant before giving up.
  useEffect(() => {
    if (!highlightAccession || filings.loadingEightK) return;
    const findNode = () => {
      const direct = eightKRefs.current[highlightAccession];
      if (direct) return direct;
      const stripped = String(highlightAccession).replace(/-/g, '');
      if (eightKRefs.current[stripped]) return eightKRefs.current[stripped];
      // Last resort: linear scan, useful if either side has extra padding.
      for (const k of Object.keys(eightKRefs.current)) {
        if (k.replace(/-/g, '') === stripped) return eightKRefs.current[k];
      }
      return null;
    };
    // Allow the layout to settle before scrolling — the 8-K list
    // hydrates from cache instantly but a fresh fetch may still be
    // mid-flight when the user navigates here from the bell.
    const attemptHighlight = (attempt = 0) => {
      const node = findNode();
      if (!node) {
        if (attempt < 8) {
          setTimeout(() => attemptHighlight(attempt + 1), 250);
        }
        return;
      }
      try {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch {
        node.scrollIntoView();
      }
      node.classList.add('filing-highlight-pulse');
      setTimeout(() => {
        node.classList.remove('filing-highlight-pulse');
      }, 3000);
    };
    const t = setTimeout(() => attemptHighlight(0), 200);
    return () => clearTimeout(t);
  }, [highlightAccession, filings.loadingEightK, filings.eightK]);

  if (viewerFiling) {
    return (
      <FilingViewer
        url={viewerFiling.url}
        label={viewerFiling.label}
        onClose={() => setViewerFiling(null)}
        user={user}
      />
    );
  }

  return (
    <div ref={containerRef} className="space-y-4" data-testid="tenk-8k-risks-tab">
      {/* Intro strip */}
      <div className="rounded-lg border bg-card p-3" data-testid="tenk-8k-risks-intro">
        <div className="flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-fuchsia-600 dark:text-fuchsia-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-bold tracking-wide mb-0.5">10-K / 8-K · Risk Factors</h3>
            <p className="text-[11px] font-bold text-muted-foreground leading-snug">
              Source documents straight from SEC EDGAR plus Massive&apos;s
              standardised 3-level risk taxonomy. Use this tab to read up
              before a buy/sell decision or to react to a fresh material
              event filing.
            </p>
          </div>
        </div>
      </div>

      {/* 8-K events */}
      <div className="rounded-lg border bg-card" data-testid="tenk-8k-risks-8k">
        <div className="px-3 py-2 border-b flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-bold">Recent 8-K Material Events</span>
          <span className="text-[10px] text-muted-foreground">· Latest 10 filings</span>
        </div>

        {/* Filter chips — mirrors the risk-factor chip bar. Only shows
            when we have 8-K events and more than one category is
            represented (a ticker with only earnings 8-Ks doesn't need
            a filter). */}
        {!filings.loadingEightK && eightKTotalCount > 0 && eightKCategoriesPresent.length > 1 && (
          <div className="px-3 py-2 border-b bg-muted/20 flex flex-wrap items-center gap-1.5" data-testid="eightk-filter-chips">
            <Layers className="w-3 h-3 text-muted-foreground" />
            <button
              type="button"
              onClick={() => setEightKFilter(null)}
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors ${
                eightKFilter === null
                  ? 'border-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-950/30 text-fuchsia-700 dark:text-fuchsia-300'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground'
              }`}
              data-testid="eightk-filter-all"
            >
              All ({eightKTotalCount})
            </button>
            {eightKCategoriesPresent.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setEightKFilter(eightKFilter === cat ? null : cat)}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors ${
                  eightKFilter === cat
                    ? 'border-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-950/30 text-fuchsia-700 dark:text-fuchsia-300'
                    : 'border-border bg-card text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`eightk-filter-${cat}`}
              >
                {EIGHTK_CATEGORY_LABEL[cat] || cat} ({eightKCategoryCounts[cat]})
              </button>
            ))}
          </div>
        )}

        <div className="px-3 py-3 space-y-2">
          {filings.loadingEightK ? (
            <p className="text-xs text-muted-foreground">Loading 8-Ks…</p>
          ) : visibleEightK && visibleEightK.length > 0 ? (
            visibleEightK.map((e, idx) => {
              const key = `8k-${idx}`;
              const isExp = expandedSections[key];
              const text = e.items_text || '';
              const isLong = text.length > 350;
              const preview = isExp ? text : text.slice(0, 350) + (isLong ? '…' : '');
              // Round 92o++++++++N++++++++ — Backend returns
              // `accession_number`; the FilingAlertsBell notifications
              // collection uses `accession`. The two payloads diverged
              // historically (Massive Form-4 vs SEC notification cron).
              // We register BOTH forms on the ref map so a bell-driven
              // deep-link lands on the matching row regardless of
              // which side trimmed the dashes/leading zeros.
              const acc = e.accession_number || e.accession || `${e.filing_date}-${idx}`;
              const accNoDashes = acc.replace(/-/g, '');
              return (
                <div
                  key={key}
                  ref={(node) => {
                    if (node) {
                      eightKRefs.current[acc] = node;
                      // Index without dashes too — Massive sometimes
                      // strips them and the bell mirrors that format
                      // for older notifications.
                      eightKRefs.current[accNoDashes] = node;
                    }
                  }}
                  className="rounded-md border bg-background/50 p-2.5"
                  data-testid={`eightk-event-${idx}`}
                  data-accession={acc}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StarFilingButton
                        ticker={ticker}
                        accession={acc}
                        formType={e.form_type || '8-K'}
                        section="8-k"
                        filingDate={e.filing_date}
                        filingUrl={e.filing_url}
                        itemCodes={Array.isArray(e.item_codes) ? e.item_codes : []}
                        title={`${ticker} ${e.form_type || '8-K'} · ${e.filing_date}${Array.isArray(e.item_codes) && e.item_codes.length ? ' · ' + e.item_codes.join(', ') : ''}`}
                        snippet={(e.items_text || '').slice(0, 400)}
                      />
                      <span className="text-[11px] font-bold uppercase tracking-wide">{e.form_type || '8-K'}</span>
                      <span className="text-[10px] text-muted-foreground">{e.filing_date}</span>
                      {Array.isArray(e.item_codes) && e.item_codes.map((c) => (
                        <span key={c} className="px-1 py-0.5 rounded text-[9px] font-bold border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-[11px] text-foreground/85 leading-relaxed whitespace-pre-line">{preview}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {isLong && (
                      <button
                        type="button"
                        onClick={() => setExpandedSections((x) => ({ ...x, [key]: !x[key] }))}
                        className="text-[11px] font-semibold text-primary hover:underline"
                      >
                        {isExp ? 'Show less' : 'Read more →'}
                      </button>
                    )}
                    {e.filing_url && (
                      <>
                        <button
                          type="button"
                          onClick={() => setViewerFiling({ url: e.filing_url, label: `${ticker} ${e.form_type || '8-K'} · ${e.filing_date}` })}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                          data-testid={`view-8k-${idx}`}
                        >
                          <Eye className="w-3 h-3" />
                          View filing
                        </button>
                        <a
                          href={e.filing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                          title="Open on SEC.gov in a new tab"
                        >
                          SEC.gov <ExternalLink className="w-3 h-3" />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          ) : eightKTotalCount > 0 && visibleEightK.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="eightk-filter-empty">
              No {EIGHTK_CATEGORY_LABEL[eightKFilter] || eightKFilter} 8-K filings in the last 10 events.{' '}
              <button
                type="button"
                onClick={() => setEightKFilter(null)}
                className="font-semibold text-primary hover:underline"
              >
                Clear filter
              </button>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">No recent 8-K events.</p>
          )}
        </div>
      </div>

      {/* 10-K sections */}
      <div className="rounded-lg border bg-card" data-testid="tenk-8k-risks-10k">
        <div className="px-3 py-2 border-b flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-bold">Most Recent 10-K</span>
          <span className="text-[10px] text-muted-foreground">· Business + Risk Factors narrative</span>
        </div>
        <div className="px-3 py-3 space-y-2">
          {filings.loadingTenK ? (
            <p className="text-xs text-muted-foreground">Loading 10-K…</p>
          ) : filings.tenK && Array.isArray(filings.tenK.sections) && filings.tenK.sections.length > 0 ? (
            filings.tenK.sections.map((s, idx) => {
              const key = `${s.section}-${idx}`;
              const isExp = expandedSections[key];
              const isLong = (s.text || '').length > 500;
              const preview = isExp ? s.text : (s.text || '').slice(0, 500) + (isLong ? '…' : '');
              return (
                <div key={key} className="rounded-md border bg-background/50 p-2.5" data-testid={`tenk-section-${s.section}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[11px] font-bold uppercase tracking-wide flex items-center gap-1.5">
                      <StarFilingButton
                        ticker={ticker}
                        accession={s.accession_number || `${ticker}-10k-${s.section}-${s.filing_date}`}
                        formType="10-K"
                        section={s.section}
                        filingDate={s.filing_date}
                        filingUrl={s.filing_url}
                        title={`${ticker} 10-K · ${s.section === 'business' ? 'Business Overview' : s.section === 'risk_factors' ? 'Risk Factors' : s.section}`}
                        snippet={(s.text || '').slice(0, 400)}
                      />
                      {s.section === 'business' ? 'Business Overview' : s.section === 'risk_factors' ? 'Risk Factors' : s.section}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      Filed {s.filing_date} · period {s.period_end}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground/85 leading-relaxed whitespace-pre-line">{preview}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {isLong && (
                      <button
                        type="button"
                        onClick={() => setExpandedSections((x) => ({ ...x, [key]: !x[key] }))}
                        className="text-[11px] font-semibold text-primary hover:underline"
                      >
                        {isExp ? 'Show less' : 'Read more →'}
                      </button>
                    )}
                    {s.filing_url && (
                      <>
                        <button
                          type="button"
                          onClick={() => setViewerFiling({ url: s.filing_url, label: `${ticker} 10-K · ${s.section === 'business' ? 'Business' : s.section === 'risk_factors' ? 'Risk Factors' : s.section}` })}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                          data-testid={`view-10k-${s.section}`}
                        >
                          <Eye className="w-3 h-3" />
                          View filing
                        </button>
                        <a
                          href={s.filing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                          title="Open on SEC.gov in a new tab"
                        >
                          SEC.gov <ExternalLink className="w-3 h-3" />
                        </a>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-muted-foreground">No 10-K sections available for this ticker.</p>
          )}
        </div>
      </div>

      {/* Risk Factors (Massive taxonomy) */}
      <div className="rounded-lg border bg-card" data-testid="tenk-8k-risks-factors">
        <div className="px-3 py-2 border-b flex items-center gap-1.5 flex-wrap">
          <AlertTriangle className="w-4 h-4 text-fuchsia-600 dark:text-fuchsia-400" />
          <span className="text-sm font-bold">Risk Factors</span>
          <span className="text-[10px] text-muted-foreground">
            · Standardised taxonomy · {totalRiskCount} {totalRiskCount === 1 ? 'row' : 'rows'}
          </span>
        </div>

        {/* YoY trend bars — only renders for tickers with 2+ distinct
            filings (skipped for IPOs / foreign filers). */}
        {!riskData.loading && riskData.trend && (
          <RiskTrendBars
            trend={riskData.trend}
            onCategoryClick={(cat) => setRiskFilter(riskFilter === cat ? null : cat)}
            selectedCategory={riskFilter}
          />
        )}

        {/* Filter chips */}
        {!riskData.loading && primaryCategories.length > 1 && (
          <div className="px-3 py-2 border-b bg-muted/20 flex flex-wrap items-center gap-1.5" data-testid="risk-filter-chips">
            <Layers className="w-3 h-3 text-muted-foreground" />
            <button
              type="button"
              onClick={() => setRiskFilter(null)}
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors ${
                riskFilter === null
                  ? 'border-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-950/30 text-fuchsia-700 dark:text-fuchsia-300'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground'
              }`}
              data-testid="risk-filter-all"
            >
              All ({totalRiskCount})
            </button>
            {primaryCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setRiskFilter(riskFilter === cat ? null : cat)}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-colors ${
                  riskFilter === cat
                    ? 'border-fuchsia-400 bg-fuchsia-50 dark:bg-fuchsia-950/30 text-fuchsia-700 dark:text-fuchsia-300'
                    : 'border-border bg-card text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`risk-filter-${cat}`}
              >
                {humaniseSlug(cat)} ({(riskGroups[cat] || []).length})
              </button>
            ))}
          </div>
        )}

        <div className="px-3 py-3 space-y-2">
          {riskData.loading ? (
            <p className="text-xs text-muted-foreground">Loading risk factors…</p>
          ) : riskData.error ? (
            <p className="text-xs text-rose-600 dark:text-rose-400">{riskData.error}</p>
          ) : visibleRows.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No risk-factor rows from Massive for this ticker yet.
            </p>
          ) : (
            visibleRows.slice(0, 50).map((r, idx) => {
              const rowKey = `risk-${idx}`;
              const isExp = riskExpanded[rowKey];
              const text = r.supporting_text || '';
              const isLong = text.length > 280;
              const preview = isExp ? text : text.slice(0, 280) + (isLong ? '…' : '');
              return (
                <div key={rowKey} className="rounded-md border bg-background/50 p-2.5" data-testid={`risk-row-${idx}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <RiskCategoryBadge
                      primary={r.primary_category}
                      secondary={r.secondary_category}
                      tertiary={r.tertiary_category}
                      taxonomyIndex={riskData.taxonomyIndex}
                    />
                    {r.filing_date && (
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                        Filed {r.filing_date}
                      </span>
                    )}
                  </div>
                  {text && (
                    <p className="text-[11px] text-foreground/85 leading-relaxed">{preview}</p>
                  )}
                  {isLong && (
                    <button
                      type="button"
                      onClick={() => setRiskExpanded((x) => ({ ...x, [rowKey]: !x[rowKey] }))}
                      className="mt-1.5 text-[11px] font-semibold text-primary hover:underline"
                    >
                      {isExp ? 'Show less' : 'Read more →'}
                    </button>
                  )}
                </div>
              );
            })
          )}
          {visibleRows.length > 50 && (
            <p className="text-[10px] text-muted-foreground italic">
              Showing top 50 of {visibleRows.length} matching rows.
            </p>
          )}
        </div>
        <div className="px-3 pb-3">
          <p className="text-[10px] text-muted-foreground italic">
            Data via Massive · risk-factors and taxonomies endpoints · cached 30 days · 10-Ks update annually.
          </p>
        </div>
      </div>
    </div>
  );
}
